import Script from 'next/script';
import { useMemo } from 'react';

import { IS_PRODUCTION, GTM_KEY, GTM_DEV_KEY } from '@/environment.js';
import { parseQueryString } from '@/queryString';

import { getGtmScript } from './scripts';

const hasProdGtmKeyOverride = () => {
  if (typeof window === 'undefined') return false;

  const queryParams = parseQueryString(window.location);
  return 'gtm_prod' in queryParams;
};

/**
 * Use query param `gtm_prod` to use the production GTM container in non-production environments
 */
export const useGtmKey = ({
  devKey = GTM_DEV_KEY(),
  prodKey = GTM_KEY(),
}: {
  /** If not specified, this defaults to the value of the NEXT_PUBLIC_GTM_DEV_KEY env var */
  devKey?: string;
  /** If not specified, this defaults to the value of the NEXT_PUBLIC_GTM_KEY env var */
  prodKey?: string;
} = {}) => {
  const shouldUseProdGtmKey = IS_PRODUCTION() || hasProdGtmKeyOverride();

  return useMemo(
    () => (shouldUseProdGtmKey ? prodKey : devKey),
    [GTM_KEY, GTM_DEV_KEY, shouldUseProdGtmKey],
  );
};

export const GTMScript = ({
  overrideKey,
}: {
  /** Allows overriding the default key (if multi-brand is required for example) */
  overrideKey?: string;
}) => {
  const defaultKey = useGtmKey();
  const key = overrideKey ?? defaultKey;
  return key ? (
    <Script id="gtm-script" type="text/javascript">
      {getGtmScript(key)}
    </Script>
  ) : null;
};
